import { useEffect } from "react";

import { Trans } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { CalendarX, Link2Off, SearchX } from "lucide-react";

import { OutcomeState } from "@/components/account/outcome-state";
import { buttonVariants } from "@/components/ui/button-variants";
import { Skeleton } from "@/components/ui/skeleton";

const VALID_TYPES = ["seat", "team", "staff", "affiliation"] as const;

interface LookupResponse {
  status?:
    | "pending"
    | "active"
    | "accepted"
    | "declined"
    | "rejected"
    | "expired"
    | "revoked"
    | "not_found";
  facilityId?: string | null;
}

const TERMINAL_STATUSES = ["declined", "rejected", "expired", "revoked"] as const;

export const Route = createFileRoute("/_authenticated/account/invitations/$type/$id")({
  component: InvitationDeepLinkPage,
});

/**
 * Magic-link landing for typed invitations (seat / team / staff / affiliation).
 *
 * Legacy did the lookup server-side via Prisma + redirected per type/status
 * (legacy src/app/[locale]/[country]/account/invitations/[type]/[id]/page.tsx):
 *   - pending          → notifications inbox (provider inbox for team /
 *                        affiliation, user inbox for seat / staff)
 *   - seat active      → billing (subscription tab)
 *   - staff active     → facility workspace
 *   - team/affiliation active → workspace hub
 *   - expired/revoked/declined → "expired" message
 *   - not found        → "not found" message
 * V1.0+ port mirrors that client-side via apps/api
 * `GET /api/account/invitations/lookup?type=...&id=...` (single-invite shape).
 */
function InvitationDeepLinkPage() {
  const { type, id } = Route.useParams();
  const navigate = useNavigate();

  const isValidType = (VALID_TYPES as readonly string[]).includes(type);

  const lookup = useQuery({
    queryKey: ["api", "/api/account/invitations/lookup", type, id],
    queryFn: () =>
      apiFetch.get<LookupResponse>(
        `/api/account/invitations/lookup?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`,
      ),
    enabled: isValidType,
    retry: false,
  });

  useEffect(() => {
    if (!isValidType) return;

    // Legacy appended ?highlight=<type>_<id>; the SPA inbox doesn't take a
    // highlight search param (V1.1) — the actionRequired tab already surfaces
    // the invite card where the user can accept/decline.
    const fallbackToInbox = () => {
      const target =
        type === "team" || type === "affiliation"
          ? "/account/provider/notifications"
          : "/account/user/notifications";
      navigate({ to: target, search: { tab: "actionRequired" }, replace: true });
    };

    if (lookup.error) {
      // 404 → render the "not found" message below without navigating away.
      if (lookup.error instanceof ApiError && lookup.error.status === 404) return;
      fallbackToInbox();
      return;
    }

    if (!lookup.data) return;

    const { status, facilityId } = lookup.data;

    if (status === "pending") {
      fallbackToInbox();
      return;
    }

    if (status === "active" || status === "accepted") {
      if (type === "seat") {
        // Legacy: seat already accepted → billing / subscription area.
        navigate({ to: "/account/user/billing", search: { tab: "subscription" }, replace: true });
        return;
      }
      if (type === "staff" && facilityId) {
        // Legacy sent accepted staff to the (facility) workspace.
        navigate({
          to: "/account/facility/$facilityId",
          params: { facilityId },
          replace: true,
        });
        return;
      }
      // team / affiliation (and staff without a facilityId) — legacy
      // redirected to the workspace hub.
      navigate({ to: "/account", replace: true });
      return;
    }

    if (status === "not_found" || (TERMINAL_STATUSES as readonly string[]).includes(status ?? "")) {
      // Terminal — render the message below, no navigation.
      return;
    }

    // Unknown or absent status — same fallback as errors: drop the user in
    // the most plausible inbox where the invite (if any) is actionable.
    fallbackToInbox();
  }, [type, id, isValidType, lookup.data, lookup.error, navigate]);

  // Terminal outcomes end on a next step, not a lone sentence (P16).
  const accountCta = (
    <Link to="/account" className={buttonVariants({ size: "touch" })}>
      <Trans>Go to my account</Trans>
    </Link>
  );

  if (!isValidType) {
    return (
      <OutcomeState
        className="min-h-[calc(100dvh-4rem)] justify-center"
        icon={Link2Off}
        tone="warning"
        title={<Trans>Invitation link not recognised.</Trans>}
        description={
          <Trans>
            Check the link in your message, or open your account to see pending invitations.
          </Trans>
        }
        actions={accountCta}
      />
    );
  }

  const is404 = lookup.error instanceof ApiError && lookup.error.status === 404;
  if (is404 || lookup.data?.status === "not_found") {
    return (
      <OutcomeState
        className="min-h-[calc(100dvh-4rem)] justify-center"
        icon={SearchX}
        title={<Trans>This invitation could not be found.</Trans>}
        description={<Trans>Ask the person who invited you to send a new invitation.</Trans>}
        actions={accountCta}
      />
    );
  }

  const status = lookup.data?.status;
  if (status && (TERMINAL_STATUSES as readonly string[]).includes(status)) {
    return (
      <OutcomeState
        className="min-h-[calc(100dvh-4rem)] justify-center"
        icon={CalendarX}
        tone="warning"
        title={<Trans>This invitation has expired or been cancelled.</Trans>}
        description={<Trans>Ask the person who invited you to send a new invitation.</Trans>}
        actions={accountCta}
      />
    );
  }

  return (
    <div className="space-y-3 p-4">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-4 w-64" />
      <p className="text-muted-foreground text-sm">
        <Trans>Looking up your invitation…</Trans>
      </p>
    </div>
  );
}
